<script setup lang="ts">
import { NuxtLink } from '#components';
import type { TMenuItem } from '~/data/header';

const defaultsStore = useDefaultsStore();
const modalsStore = useModalsStore();

const props = defineProps<{ item: TMenuItem }>();
const isOpen = ref(false);
const activeImg = ref<string | undefined>(props.item?.img);

const itemColumnsList = computed(() => {
    if (!props.item?.groups) return;
    return props.item.groups;
});


function handleOpenItem() {
    isOpen.value = true;
};

function handleCloseItem() {
    isOpen.value = false;
};

function handleOnClickLink() {
    isOpen.value = false;
};

function handleChangeImg(path: string | undefined) {
    if (path) activeImg.value = path;
    else activeImg.value = props.item?.img;
};

function handleOpenModal() {
  modalsStore.openModal('CTA');
};
</script>

<template>
    <div class="h-full" @mouseenter="handleOpenItem" @mouseleave="handleCloseItem">
        <component
            :is="itemColumnsList ? 'div' : NuxtLink"
            :to="props.item.path"
            class="h-full flex items-center gap-2 px-2 text-body1-semibold text-gray-500 cursor-pointer select-none"
            :class="isOpen && 'text-primary-500!'"
        >
            <span>{{ props.item.label }}</span>
            <Icon 
                v-if="props.item?.groups"
                name="solar:alt-arrow-down-linear" 
                size="16" 
                class="transition-transform duration-300" 
                :class="isOpen && '-rotate-180'"
            />
        </component>
        <Transition name="item">
            <div v-if="itemColumnsList && isOpen" class="item">
                <div class="bg-white flex divide-x divide-gray-300 py-6 px-4">
                    <div 
                        v-for="group, index in itemColumnsList"
                        :key="index"
                        class="w-84.25 min-h-full flex flex-col px-8"
                    >
                        <div v-for="cate in group" :key="cate.title">
                            <span class="block text-body2 text-gray-400 mb-3">{{ cate.title }}</span>
                            <div class="flex flex-col gap-1">
                                <NuxtLink
                                    v-for="link in cate.items"
                                    :key="link.title"
                                    :to="link.path" 
                                    :target="link?.targetBlank ? '_blank' : undefined"
                                    class="flex items-center gap-2 p-3 cursor-pointer select-none rounded-lg 
                                        transition-colors duration-300 hover:bg-primary-50 text-body2-semibold"
                                    @click="handleOnClickLink"
                                    @mouseenter="handleChangeImg(link.img)"
                                    @mouseleave="handleChangeImg(undefined)"
                                >
                                    <Icon :name="link.icon" size="24" class="text-gray-400" />
                                    {{ link.title }}
                                </NuxtLink>
                            </div>
                        </div>
                    </div>
                    <div class="hidden lg:block w-75 min-h-full flex-center px-8 mx-auto">
                        <div class="w-full aspect-square bg-primary-50 rounded-xl flex-center">
                            <Transition name="fade" mode="out-in">
                                <img
                                    :key="activeImg"
                                    :src="activeImg"
                                    :alt="activeImg"
                                    class="max-w-full max-h-full object-cover"
                                    loading="lazy"
                                />
                            </Transition>
                        </div>
                    </div>
                </div>
                <div class="w-full min-w-190 py-4 px-12 flex items-center justify-between bg-gray-100 gap-10">
                    <span class="text-body2 text-gray-500">نیاز به مشاوره دارید؟ کارشناسان ما همیشه آماده پاسخگویی به سوالات شما هستند.</span>
                    <Button
                        type="Thirdly"
                        :append-icon="{ name: 'solar:round-arrow-left-linear' }"
                        class="px-0!"
                        @click="handleOpenModal"
                    >
                        مشاوره رایگان
                    </Button>
                </div>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.item-enter-active,
.item-leave-active {
  transition: all 0.25s ease;
}
.item-enter-from,
.item-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.item-enter-to,
.item-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.item {
    /* TRICK: This element shouldnt have a top shadow */
    top: v-bind('defaultsStore.headerHeight + "px"');
    @apply
    absolute right-1/2 translate-x-1/2 z-100 rounded-b-xl overflow-hidden
    lg:shadow-[0_15px_15px_0_#111C2D1F]
}
.item::before {
    @apply
    content-[''] hidden lg:block absolute w-[calc(100%+40px)] h-16 -top-16 -left-5 
    shadow-gray-s
}
</style>
